import { useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import {
  adderResumeSelector,
  loadingProductSelector,
  productPackageProductsSelector,
  tariffsState,
  productPackageTotalPriceSelector,
  checkIfThePackageHasInactiveProductSelector,
  productSelectionStateSelector,
} from '@src/features/product';
import { useCallback, useMemo } from 'react';
import usePackageIdParam from './usePackageIdParam';

export const usePackage = () => {
  const widgetId = useWidgetId();
  const packageId = usePackageIdParam()!;

  const {
    selectedTariffs,
    selectedTariffHour,
    productId: selectedProductId,
  } = useAppSelector(adderResumeSelector(widgetId));

  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const loadingProduct = useAppSelector(loadingProductSelector(selectedProductId));

  const productPackageProducts = useAppSelector(productPackageProductsSelector(packageId));
  const packageTotalCents = useAppSelector(productPackageTotalPriceSelector(widgetId, packageId!));
  const hasInactiveProduct = useAppSelector(checkIfThePackageHasInactiveProductSelector(packageId));
  const productSelectionState = useAppSelector(productSelectionStateSelector(widgetId));

  const countFilledProducts = useMemo(
    () =>
      Object.values(productSelectionState).reduce((acc, cur) => {
        if ('currentProductAdderStep' in cur && cur.currentProductAdderStep === 'finish') {
          return acc + 1;
        }

        return acc;
      }, 0),
    [productSelectionState],
  );

  const selectedProduct = useMemo(
    () => productPackageProducts?.find((ppp) => ppp.productId === selectedProductId)?.product,
    [productPackageProducts, selectedProductId],
  );

  const isProductFilled = useCallback(
    (productId: string) => productSelectionState[productId]?.currentProductAdderStep === 'finish',
    [productSelectionState[selectedProductId]],
  );

  const forwardedValue = useCallback(
    <T>(productId: string, value: T) => (productId === selectedProductId ? value : undefined),
    [selectedProductId],
  );

  const getSelectedDate = useCallback(
    (productId: string) =>
      productSelectionState[productId]?.tariffsState?.selectedDate ?? forwardedValue(productId, selectedDate),
    [productSelectionState, selectedDate],
  );

  const getSelectedTime = useCallback(
    (productId: string) =>
      productSelectionState[productId]?.selectedTariffHour?.formattedTime ??
      forwardedValue(productId, selectedTariffHour?.formattedTime),
    [productSelectionState, selectedTariffHour],
  );

  const getSelectedTariffs = useCallback(
    (productId: string) =>
      productSelectionState[productId]?.tariffsState?.listOfTariffsPerType.filter((tf) => tf.selectedQuantity) ||
      forwardedValue(productId, selectedTariffs),
    [productSelectionState, selectedTariffs],
  );

  const shouldProductBeDisabled = useCallback(
    (productId: string) => {
      if (
        selectedProductId &&
        selectedProductId !== productId &&
        (!!getSelectedDate(selectedProductId) || !!getSelectedTariffs(selectedProductId)?.length) &&
        !isProductFilled(productId)
      ) {
        return true;
      }

      return false;
    },
    [selectedProductId, getSelectedDate, isProductFilled],
  );

  return {
    isProductFilled,
    getSelectedDate,
    getSelectedTime,
    getSelectedTariffs,
    selectedProductId,
    countFilledProducts,
    selectedProduct,
    loadingProduct,
    shouldProductBeDisabled,
    packageTotalCents,
    hasInactiveProduct,
  };
};

export default usePackage;
